import { styled } from '@mui/material/styles';
import { useCallback, useMemo, type JSX } from 'react';
import { PopupMenu } from '../../../../components/PopupMenu/PopupMenu';
import { useSelectedCurrency } from '../../../../hooks/useSelectedCurrency';
import { ViewModel } from '../../../../models/view.model';
import { kpiCardNameToPermissionKey } from '../../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../../services/PermissionService';
import { Kpi, KPI_COMPONENTS, SelectItem } from '../../../../types';
import { SubSection } from '../../../CompanyProfiles/Summary/components/SubSection';
import { useKPIs } from '../../hooks/useKPIs';
import { useView } from '../../hooks/useView';
import { CardList, ICardProps } from './CardList';

const Section = styled('section')`
  margin: 1rem 2rem;
`;

const SECTION_ID = 'kpis';

export const KPISection = ({ view }: { id: string; view: ViewModel }): JSX.Element | null => {
  const { selectedCurrency } = useSelectedCurrency();
  const { kpis, previousQuarterKPIs, kpiMetas } = useKPIs(view);
  const { getComponentsOrder, setComponentsOrder, getSection } = useView();
  const isAuditView = PermissionService.get().isAuditor();

  const handleChange2 = useCallback(
    (selectedItems: SelectItem[]) => {
      setComponentsOrder(
        SECTION_ID,
        selectedItems.map((item) => String(item.id))
      );
    },
    [setComponentsOrder]
  );

  const handleDelete = (id: string) => {
    const componentOrder = getComponentsOrder(SECTION_ID);
    const updatedComponentsOrder = componentOrder.filter((cId) => cId !== id);
    setComponentsOrder(SECTION_ID, updatedComponentsOrder);
  };

  const handleReorder = (reorderedIds: string[]) => {
    setComponentsOrder(SECTION_ID, reorderedIds);
  };

  const selectedItems: SelectItem[] = !kpis
    ? []
    : getComponentsOrder(SECTION_ID).map((id) => {
        const kpi = kpis[id as KPI_COMPONENTS];
        return {
          id: kpi?.id as string,
          value: kpi?.title as string,
        };
      });

  const kpisData =
    !kpis || !previousQuarterKPIs
      ? []
      : getComponentsOrder(SECTION_ID).reduce((acc, id) => {
          const kpi: Kpi | undefined = kpis[id as KPI_COMPONENTS];
          if (!kpi || !kpi.id) {
            return acc;
          }
          const kpiLastEndedQuoter = previousQuarterKPIs[id as KPI_COMPONENTS];
          const indicator = ((kpi?.value - kpiLastEndedQuoter?.value) / kpiLastEndedQuoter?.value) * 100;
          const { formatted, title, description } = kpi;
          acc.push({
            id: kpi.id,
            value: formatted,
            indicator: isNaN(indicator) ? null : indicator,
            title,
            description,
          });
          return acc;
        }, [] as ICardProps[]);

  const options = useMemo(
    () =>
      (!kpiMetas
        ? []
        : kpiMetas
            .filter((kpi) => PermissionService.get().hasPermission(kpiCardNameToPermissionKey(kpi.id)))
            .map((kpi) => ({ id: kpi.id, value: kpi.title }))
      ).sort((a, b) => (a.value > b.value ? 1 : b.value > a.value ? -1 : 0)),
    [kpiMetas]
  );

  return (
    <Section key={selectedCurrency}>
      <SubSection
        title={getSection(SECTION_ID).title}
        actionComponent={
          !isAuditView ? (
            <PopupMenu
              items={options}
              onChange={handleChange2}
              selectedItems={selectedItems}
              label={'Select KPIs'}
              style={{ marginLeft: 'auto' }}
            />
          ) : undefined
        }
        collapseProps={{ collapsible: true }}
        noCard
        style={{ margin: 0 }}
      >
        <CardList
          kpis={kpisData}
          onReorder={handleReorder}
          onDelete={handleDelete}
          disableDrag={isAuditView}
        />
      </SubSection>
    </Section>
  );
};
